import { useEffect, useState } from 'react';
import { colors } from '../theme';

// First-run install card. On Android we wait for `beforeinstallprompt` and
// surface a one-tap install button. On iOS Safari (which doesn't fire that
// event and has no JS API to open Share sheet) we show manual instructions.
// Hides itself when running in standalone mode — the tell-tale that A2HS
// already happened.

const DISMISS_KEY = 'hive.a2hs.dismissed.v1';

type DeferredPrompt = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

function inStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS uses `navigator.standalone`; others use display-mode media query.
  const iosStandalone = (navigator as any).standalone === true;
  const mq =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return iosStandalone || mq;
}

function isIOS(): boolean {
  const ua = navigator.userAgent;
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports itself as Mac — detect via touch + platform.
  const iPadOS =
    navigator.platform === 'MacIntel' && typeof (navigator as any).maxTouchPoints === 'number' && (navigator as any).maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

export function A2HSPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<DeferredPrompt | null>(null);
  const ios = isIOS();

  useEffect(() => {
    if (inStandalone()) return;
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return;
    } catch {}

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferred(e as DeferredPrompt);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // On iOS, `beforeinstallprompt` never fires — show the prompt immediately
    // (after a short delay so it doesn't flash before first paint).
    if (ios) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      };
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [ios]);

  // Re-hide if the user ends up installing through the native UI path.
  useEffect(() => {
    const onInstalled = () => {
      setVisible(false);
      try {
        localStorage.setItem(DISMISS_KEY, '1');
      } catch {}
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  function dismiss() {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {}
  }

  async function doInstall() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div className="a2hs-scrim">
      <div className="a2hs-card">
        <h2>Install Cupwire</h2>
        <p className="a2hs-sub">
          Running in your browser works, but installing gives you a proper icon, persistent mic and
          location permissions, and a full-screen experience.
        </p>

        {ios ? (
          <ol className="a2hs-steps">
            <li>
              Tap the <strong>Share</strong> icon in Safari (the square with the up arrow).
            </li>
            <li>
              Scroll and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Confirm <strong>Add</strong>. Cupwire appears on your home screen.
            </li>
          </ol>
        ) : (
          <p className="a2hs-sub">Tap install to add Cupwire to your home screen.</p>
        )}

        <div className="a2hs-actions">
          <button className="a2hs-skip" onClick={dismiss}>
            Not now
          </button>
          {!ios && deferred && (
            <button className="a2hs-install" onClick={doInstall}>
              Install
            </button>
          )}
        </div>
      </div>

      <style>{`
        .a2hs-scrim {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.7);
          z-index: 3000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }
        .a2hs-card {
          background: ${colors.surface};
          border-radius: 14px;
          padding: 22px;
          max-width: 400px;
          width: 100%;
        }
        .a2hs-card h2 {
          margin: 0 0 6px;
          color: ${colors.text};
          font-size: 22px;
        }
        .a2hs-sub { color: ${colors.textMuted}; font-size: 14px; line-height: 1.5; margin: 0 0 14px; }
        .a2hs-steps {
          color: ${colors.text};
          font-size: 14px;
          line-height: 1.6;
          padding-left: 20px;
          margin: 0 0 18px;
        }
        .a2hs-steps strong { color: ${colors.accent}; }
        .a2hs-actions { display: flex; gap: 10px; }
        .a2hs-skip {
          flex: 1;
          background: transparent;
          color: ${colors.textMuted};
          border: 1px solid ${colors.divider};
          padding: 12px;
          border-radius: 10px;
          font-size: 14px;
        }
        .a2hs-install {
          flex: 1;
          background: ${colors.accent};
          color: ${colors.bg};
          border: none;
          padding: 12px;
          border-radius: 10px;
          font-size: 15px;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
}
